/**
 * useTemplateGenerator Hook
 * 
 * Manages AI template generation state and logic
 * Handles skills selection, question configuration, and n8n workflow integration
 */

import { useState, useEffect, useMemo, useRef } from "react";
import { message, FormInstance, Modal } from "antd";
import interviewRoundTemplateService, { 
  GroupedQuestionStyles, 
  QuestionStyleOption,
  GenerateTemplateRequest 
} from "../../../../../../../Service/interview-round-template.service";
import { Requirements } from "../../../../../../../Entities/JobDescription";
import { RoundFormValues, UseTemplateGeneratorReturn, GenerationStep } from "../types";
import { 
  QUESTION_TYPE_TO_ANSWER_TYPE,
  ANSWER_TYPE_TO_QUESTION_TYPE,
  SHARED_STYLES 
} from "../constants";
import { DEFAULT_PASS_PERCENTAGE } from "../../../../../utils/constants";
import { withRetry } from "../../../../../utils/errorHandling";

/**
 * Generation steps for the progress indicator
 * Note: "eval" step is conditionally included based on question type
 */
const BASE_GENERATION_STEPS: GenerationStep[] = [
  { key: "skills", label: "Analyzing selected skills" },
  { key: "prompt", label: "Creating question generation prompt" },
  { key: "finalize", label: "Finalizing template" },
];

const EVAL_STEP: GenerationStep = { key: "eval", label: "Generating evaluation criteria" };

/**
 * Get generation steps based on question type
 * MCQ doesn't need evaluation criteria, other types do
 */
const getGenerationSteps = (questionType: string): GenerationStep[] => {
  // MCQ doesn't need evaluation criteria
  if (questionType === "MCQ") {
    return BASE_GENERATION_STEPS;
  }
  // For Descriptive, Coding, Mixed - include evaluation step
  return [
    BASE_GENERATION_STEPS[0], // skills
    BASE_GENERATION_STEPS[1], // prompt
    EVAL_STEP,                // eval
    BASE_GENERATION_STEPS[2], // finalize
  ];
};

interface UseTemplateGeneratorProps {
  form: FormInstance<RoundFormValues>;
  requirements: Requirements[];
  formValues: RoundFormValues | undefined;
  onSampleQuestionsReset: () => void;
}

export const useTemplateGenerator = ({
  form,
  requirements,
  formValues,
  onSampleQuestionsReset,
}: UseTemplateGeneratorProps): UseTemplateGeneratorReturn => {
  // Skills state
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [customSkillInput, setCustomSkillInput] = useState("");
  
  // Question configuration state
  const [questionStyles, setQuestionStyles] = useState<GroupedQuestionStyles>({});
  const [selectedQuestionType, setSelectedQuestionType] = useState<string>("Mixed");
  const [selectedQuestionStyle, setSelectedQuestionStyle] = useState<string>("");
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>("");
  
  // Loading states
  const [isLoadingStyles, setIsLoadingStyles] = useState(false);
  const [isGeneratingTemplate, setIsGeneratingTemplate] = useState(false);
  
  // Generation progress state
  const [generationStep, setGenerationStep] = useState<number>(-1);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // AbortController for cancelling template generation
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Cleanup on unmount - cancel any pending requests and intervals
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []);

  // Load question styles on mount
  useEffect(() => {
    const loadQuestionStyles = async () => {
      setIsLoadingStyles(true);
      try {
        const styles = await interviewRoundTemplateService.getQuestionStyles();
        setQuestionStyles(styles);
      } catch (error) {
        console.error("Error loading question styles:", error);
      } finally {
        setIsLoadingStyles(false);
      }
    };
    loadQuestionStyles();
  }, []);

  // Get filtered question styles based on selected question type
  const filteredQuestionStyles: QuestionStyleOption[] = useMemo(() => {
    if (!selectedQuestionType || !questionStyles[selectedQuestionType]) {
      return [];
    }
    return questionStyles[selectedQuestionType];
  }, [selectedQuestionType, questionStyles]);

  // Get generation steps based on question type (MCQ doesn't need evaluation criteria)
  const generationSteps = useMemo(() => {
    return getGenerationSteps(selectedQuestionType);
  }, [selectedQuestionType]);

  // Check if template generator form is ready for generation
  const isTemplateGeneratorReady = useMemo(() => {
    return Boolean(
      selectedSkills.length > 0 &&
      selectedQuestionType &&
      selectedQuestionStyle &&
      selectedDifficulty
    );
  }, [selectedSkills, selectedQuestionType, selectedQuestionStyle, selectedDifficulty]);

  // Handle skill toggle
  const handleSkillToggle = (skill: string) => {
    setSelectedSkills(prev => 
      prev.includes(skill) 
        ? prev.filter(s => s !== skill)
        : [...prev, skill]
    );
  };

  // Handle adding custom skill - accepts available skills from parent for duplicate checking
  const handleAddCustomSkill = (availableSkills: string[]) => {
    const trimmedSkill = customSkillInput.trim();
    if (!trimmedSkill) return;

    // Case-insensitive check against both selectedSkills and availableSkills
    const trimmedLower = trimmedSkill.toLowerCase();
    const isDuplicateInSelected = selectedSkills.some(
      skill => skill.toLowerCase() === trimmedLower
    );
    const isDuplicateInAvailable = availableSkills.some(
      skill => skill.toLowerCase() === trimmedLower
    );

    if (isDuplicateInSelected || isDuplicateInAvailable) {
      message.warning(`Skill "${trimmedSkill}" already exists`);
      return;
    }

    setSelectedSkills(prev => [...prev, trimmedSkill]);
    setCustomSkillInput("");
  };

  // Handle select all skills - accepts available skills from parent
  const handleSelectAllSkills = (availableSkills: string[]) => {
    // Combine all available skills with any custom skills already selected
    const allSkills = new Set([...availableSkills, ...selectedSkills]);
    setSelectedSkills(Array.from(allSkills));
  };

  // Handle clear all skills
  const handleClearAllSkills = () => {
    setSelectedSkills([]);
  };

  // Handle question type change from Column 1 - reset question style when type changes
  const handleQuestionTypeChange = (type: string) => {
    const formValue = QUESTION_TYPE_TO_ANSWER_TYPE[type] || "mixed";
    const currentFormValue = form.getFieldValue("questionType");
    
    setSelectedQuestionType(type);
    setSelectedQuestionStyle(""); // Reset style when type changes
    
    // Sync with Column 2 form - only if different to prevent loops
    if (formValue !== currentFormValue) {
      form.setFieldValue("questionType", formValue);
    }
  };

  // Handle question type change from Column 2 form (sync back to Column 1)
  const handleQuestionTypeChangeFromForm = (formQuestionType: string) => {
    const column1Type = ANSWER_TYPE_TO_QUESTION_TYPE[formQuestionType] || "Mixed";
    
    // Only process if actually different to prevent infinite loops
    if (column1Type === selectedQuestionType) {
      return;
    }
    
    // Check if there's existing content that would be affected
    const currentValues = form.getFieldsValue();
    const hasExistingContent = Boolean(
      currentValues.title?.trim() ||
      currentValues.questionGenerationPrompt?.trim() ||
      currentValues.evaluationCriteria?.trim()
    );
    
    // If no existing content, just sync without confirmation
    if (!hasExistingContent) {
      setSelectedQuestionType(column1Type);
      setSelectedQuestionStyle(""); // Reset style for new question type
      return;
    }
    
    // Show confirmation modal for user to choose action
    Modal.confirm({
      title: "Question Type Changed",
      content: "Changing the Question Type affects the template generation. Question Style will need to be reselected.\n\nWhat would you like to do with your current content?",
      okText: "Reset All & Regenerate",
      cancelText: "Keep Content",
      width: 480,
      onOk: () => {
        // Reset all Column 1 settings and Column 2 form fields
        setSelectedQuestionType(column1Type);
        setSelectedQuestionStyle("");
        setSelectedDifficulty("");
        
        // Reset Column 2 form fields (keep question type as user selected)
        form.setFieldsValue({
          title: "",
          questionGenerationPrompt: "",
          evaluationCriteria: "",
          questionType: formQuestionType,
          passPercentage: formValues?.passPercentage ?? DEFAULT_PASS_PERCENTAGE,
          isMustQualify: formValues?.isMustQualify ?? true,
        });
        
        message.info("All fields reset. Please configure and generate a new template.");
      },
      onCancel: () => {
        // Keep existing content, just sync the question type and reset style
        setSelectedQuestionType(column1Type);
        setSelectedQuestionStyle(""); // Still need to reset style
        
        message.info("Content preserved. Please select a Question Style to regenerate template if needed.");
      },
    });
  };

  // Handle question style change
  const handleQuestionStyleChange = (style: string) => {
    setSelectedQuestionStyle(style);
  };

  // Handle difficulty change
  const handleDifficultyChange = (difficulty: string) => {
    setSelectedDifficulty(difficulty);
  };

  // Check if Column 2 form has meaningful user-entered content
  const hasManualEdits = (): boolean => {
    const currentValues = form.getFieldsValue();
    const title = currentValues.title?.trim() || "";
    const prompt = currentValues.questionGenerationPrompt?.trim() || "";
    const evaluation = currentValues.evaluationCriteria?.trim() || "";
    
    // Consider it has manual edits if any of these fields have content
    return title.length > 0 || prompt.length > 20 || evaluation.length > 20;
  };

  // Internal function to execute template generation
  const executeTemplateGeneration = async () => {
    // Cancel any previous pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Clear any previous progress interval
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Capture current steps count for this generation session
    const currentSteps = getGenerationSteps(selectedQuestionType);
    const stepsCount = currentSteps.length;

    setIsGeneratingTemplate(true);
    setGenerationStep(0); // Start at first step
    
    // Simulate progress while API call is in flight
    progressIntervalRef.current = setInterval(() => {
      setGenerationStep(prev => {
        // Only advance if we haven't reached the last step
        if (prev < stepsCount - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 1200); // Move to next step every 1.2 seconds

    try {
      // Build requirements strings from requirements data
      const mustMatchRequirements = requirements
        .filter(req => req.mustMatch)
        .map(req => req.description)
        .join("\n- ");
      
      const otherRequirements = requirements
        .filter(req => !req.mustMatch)
        .map(req => req.description)
        .join("\n- ");

      const request: GenerateTemplateRequest = {
        must_match_requirements: mustMatchRequirements ? `- ${mustMatchRequirements}` : "",
        other_requirements: otherRequirements ? `- ${otherRequirements}` : undefined,
        skills: selectedSkills.join(", "),
        question_type: selectedQuestionType as "MCQ" | "Descriptive" | "Coding" | "Mixed",
        question_style: selectedQuestionStyle,
        difficulty_level: selectedDifficulty as "Beginner" | "Intermediate" | "Advanced" | "Expert",
      };

      // Use withRetry for resilience (3 attempts with 1s delay)
      const response = await withRetry(
        () => interviewRoundTemplateService.generateTemplate(request, signal),
        3,
        1000,
        (error) => {
          // Don't retry cancelled requests
          if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
            return false;
          }
          // Retry on server errors (5xx)
          const status = error?.response?.status;
          return !status || status >= 500;
        }
      );
      
      // Detailed debugging
      console.log("=== TEMPLATE GENERATION DEBUG START ===");
      console.log("[useTemplateGenerator] Raw response:", response);
      console.log("[useTemplateGenerator] Response type:", typeof response);
      console.log("[useTemplateGenerator] Response keys:", response ? Object.keys(response) : "null/undefined");
      
      // Log all nested values to find the right field
      if (response && typeof response === 'object') {
        for (const [key, value] of Object.entries(response)) {
          console.log(`[useTemplateGenerator] response.${key}:`, typeof value === 'string' ? value.substring(0, 100) : value);
        }
      }

      // Try to extract from different possible structures
      let generatedPrompt = "";
      let generatedEvaluation = "";
      let generatedTitle = "";

      // Check all possible field names in the response (n8n workflows can use various naming conventions)
      const possiblePromptKeys = [
        'questionGenerationPrompt', 'question_generation_prompt', 'prompt', 
        'custom_ai_prompt', 'customAiPrompt', 'questionPrompt', 'question_prompt',
        'generationPrompt', 'generation_prompt', 'aiPrompt', 'ai_prompt',
        'interviewPrompt', 'interview_prompt', 'assessmentPrompt', 'assessment_prompt',
        'Prompt', 'QuestionPrompt', 'GenerationPrompt'
      ];
      
      const possibleEvalKeys = [
        'evaluationCriteria', 'evaluation_criteria', 'evaluation', 'criteria',
        'evaluationPrompt', 'evaluation_prompt', 'evalCriteria', 'eval_criteria',
        'evaluationCriteriaPrompt', 'evaluation_criteria_prompt', 'evalPrompt', 'eval_prompt',
        'scoringCriteria', 'scoring_criteria', 'gradingCriteria', 'grading_criteria',
        'assessmentCriteria', 'assessment_criteria', 'rubric', 'Rubric',
        'EvaluationCriteria', 'Evaluation', 'Criteria'
      ];
      
      const possibleTitleKeys = [
        'suggestedTitle', 'suggested_title', 'title', 'round_title', 
        'roundTitle', 'roundName', 'round_name', 'name',
        'Title', 'RoundTitle', 'RoundName', 'assessmentTitle', 'assessment_title'
      ];

      // Search in response directly and in nested data/result/output
      const searchLocations = [
        response, 
        response?.data, 
        response?.result, 
        response?.output,
        response?.template,
        response?.generated,
        response?.content
      ];
      
      for (const location of searchLocations) {
        if (!location || typeof location !== 'object') continue;
        
        // Find prompt
        if (!generatedPrompt) {
          for (const key of possiblePromptKeys) {
            if (location[key]) {
              generatedPrompt = location[key];
              console.log(`[useTemplateGenerator] Found prompt at location.${key}`);
              break;
            }
          }
        }
        
        // Find evaluation
        if (!generatedEvaluation) {
          for (const key of possibleEvalKeys) {
            if (location[key]) {
              generatedEvaluation = location[key];
              console.log(`[useTemplateGenerator] Found evaluation at location.${key}`);
              break;
            }
          }
        }
        
        // Find title
        if (!generatedTitle) {
          for (const key of possibleTitleKeys) {
            if (location[key]) {
              generatedTitle = location[key];
              console.log(`[useTemplateGenerator] Found title at location.${key}`);
              break;
            }
          }
        }
      }

      // Fallback: Try to find values by analyzing all string properties in response
      // If evaluation not found, look for second-longest string that looks like criteria
      if (!generatedEvaluation && response && typeof response === 'object') {
        const stringValues: Array<{key: string, value: string, length: number}> = [];
        
        const collectStrings = (obj: any, prefix: string = "") => {
          if (!obj || typeof obj !== 'object') return;
          for (const [key, value] of Object.entries(obj)) {
            if (typeof value === 'string' && value.length > 50) {
              stringValues.push({ key: prefix + key, value, length: value.length });
            } else if (typeof value === 'object') {
              collectStrings(value, prefix + key + ".");
            }
          }
        };
        collectStrings(response);
        
        // Sort by length descending
        stringValues.sort((a, b) => b.length - a.length);
        
        console.log("[useTemplateGenerator] All string values found:", 
          stringValues.map(s => ({ key: s.key, length: s.length, preview: s.value.substring(0, 50) }))
        );
        
        // If we have at least 2 strings and no eval yet, assume second one is evaluation
        if (stringValues.length >= 2 && !generatedEvaluation) {
          // First one should be prompt (if not already found)
          if (!generatedPrompt) {
            generatedPrompt = stringValues[0].value;
            console.log(`[useTemplateGenerator] Fallback: Using ${stringValues[0].key} as prompt`);
          }
          // Second one should be evaluation criteria
          generatedEvaluation = stringValues[1].value;
          console.log(`[useTemplateGenerator] Fallback: Using ${stringValues[1].key} as evaluation criteria`);
        }
      }

      // Fallback title
      if (!generatedTitle) {
        generatedTitle = `${selectedSkills.slice(0, 2).join(" & ")} Assessment`;
      }

      console.log("[useTemplateGenerator] Final extracted values:", {
        generatedPrompt: generatedPrompt ? generatedPrompt.substring(0, 100) + "..." : "(empty)",
        generatedEvaluation: generatedEvaluation ? generatedEvaluation.substring(0, 100) + "..." : "(empty)",
        generatedTitle,
      });
      console.log("=== TEMPLATE GENERATION DEBUG END ===");

      // Show debug message to user with actual response keys
      const responseKeys = response ? Object.keys(response).join(", ") : "none";
      
      if (!generatedPrompt) {
        message.warning(`No Question Generation Prompt found. Response keys: ${responseKeys}`);
      }
      if (!generatedEvaluation) {
        // Show what keys we got so user can report back
        message.info(`No Evaluation Criteria in response. Keys: ${responseKeys}`);
      }

      // Pre-fill the form (Column 2) with the generated template
      console.log("[useTemplateGenerator] Setting form values now...");
      
      const questionTypeValue = QUESTION_TYPE_TO_ANSWER_TYPE[selectedQuestionType] || "mixed";
      
      // Preserve existing evaluationCriteria if the template doesn't provide one
      // This prevents overwriting user-entered evaluation criteria when regenerating
      const currentEvaluationCriteria = form.getFieldValue("evaluationCriteria") || "";
      const evaluationCriteriaToSet = generatedEvaluation || currentEvaluationCriteria;
      
      const formData = {
        title: generatedTitle,
        questionType: questionTypeValue,
        questionGenerationPrompt: generatedPrompt,
        evaluationCriteria: evaluationCriteriaToSet,
        passPercentage: formValues?.passPercentage ?? DEFAULT_PASS_PERCENTAGE,
        isMustQualify: formValues?.isMustQualify ?? true,
      };
      
      console.log("[useTemplateGenerator] Form data to set:", {
        ...formData,
        questionGenerationPrompt: formData.questionGenerationPrompt?.substring(0, 50) + "...",
        evaluationCriteria: formData.evaluationCriteria?.substring(0, 50) + "...",
      });
      
      // Use setFields API which is more reliable for setting multiple fields
      form.setFields([
        { name: "questionType", value: questionTypeValue },
        { name: "title", value: generatedTitle },
        { name: "questionGenerationPrompt", value: generatedPrompt },
        { name: "evaluationCriteria", value: evaluationCriteriaToSet },
        { name: "passPercentage", value: formData.passPercentage },
        { name: "isMustQualify", value: formData.isMustQualify },
      ]);
      
      // Also use setFieldsValue as backup
      form.setFieldsValue(formData);
      
      // Wait for React to process
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Verify and retry if needed
      const currentFormValues = form.getFieldsValue();
      console.log("[useTemplateGenerator] Form values after setting:", {
        title: currentFormValues.title,
        questionType: currentFormValues.questionType,
        questionGenerationPrompt: currentFormValues.questionGenerationPrompt?.substring(0, 50) + "...",
        evaluationCriteria: currentFormValues.evaluationCriteria?.substring(0, 50) + "...",
      });
      
      // If evaluationCriteria still not set, force it again
      if (evaluationCriteriaToSet && !currentFormValues.evaluationCriteria) {
        console.warn("[useTemplateGenerator] evaluationCriteria not set, forcing...");
        form.setFields([{ name: "evaluationCriteria", value: evaluationCriteriaToSet }]);
        form.setFieldValue("evaluationCriteria", evaluationCriteriaToSet);
      }

      // Clear progress interval and mark all steps complete
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      setGenerationStep(stepsCount); // All steps complete
      
      message.success("Template generated successfully! Review and customize as needed.");
      
      // Reset sample questions since form has changed
      onSampleQuestionsReset();
      
    } catch (error: any) {
      // Clear progress interval on error
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
      // Don't show error for cancelled requests
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        console.log("[useTemplateGenerator] Request cancelled");
        return;
      }
      
      console.error("[useTemplateGenerator] Template generation error:", error);
      const errorMessage = error?.response?.data?.message 
        || error?.message 
        || "Failed to generate template. Please try again.";
      message.error(errorMessage);
    } finally {
      setIsGeneratingTemplate(false);
      abortControllerRef.current = null;
    }
  };

  // Generate template using AI (via n8n workflow)
  // Shows confirmation if user has made manual edits
  const handleGenerateTemplate = async () => {
    if (!isTemplateGeneratorReady) {
      message.warning("Please fill in all required fields (Skills, Question Type, Question Style, and Difficulty Level)");
      return;
    }

    // Check if user has made manual edits that would be overwritten
    if (hasManualEdits()) {
      Modal.confirm({
        title: "Overwrite existing content?",
        content: "Generating a new template will replace your current Round Title, Question Generation Prompt, and Evaluation Criteria. Do you want to continue?",
        okText: "Yes, Generate",
        cancelText: "Cancel",
        onOk: () => {
          executeTemplateGeneration();
        },
      });
    } else {
      // No manual edits, generate directly
      await executeTemplateGeneration();
    }
  };

  // Cancel any pending template generation request
  const cancelGeneration = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setIsGeneratingTemplate(false);
    setGenerationStep(-1);
  };

  // Reset all state
  const resetState = () => {
    // Cancel any pending request first
    cancelGeneration();
    
    setSelectedSkills([]);
    setCustomSkillInput("");
    setSelectedQuestionType("Mixed");
    setSelectedQuestionStyle("");
    setSelectedDifficulty("");
    setGenerationStep(-1);
  };

  // Initialize from job description skills (for new rounds)
  const initializeFromJobDescription = (skills: string[]) => {
    setSelectedSkills(skills.length > 0 ? skills : []);
    setSelectedQuestionType("Mixed");
    setSelectedQuestionStyle("");
    setSelectedDifficulty("");
  };

  // Initialize from editing round (for edit mode)
  const initializeFromEditingRound = (
    answerType: string | undefined,
    availableSkills: string[]
  ) => {
    // Map answerType to question type for Column 1
    const answerTypeToQuestionType: Record<string, string> = {
      mcq: "MCQ",
      descriptive: "Descriptive",
      coding: "Coding",
      mixed: "Mixed",
    };
    
    const normalizedAnswerType = (answerType || "mixed").toLowerCase().trim();
    const questionType = answerTypeToQuestionType[normalizedAnswerType] || "Mixed";
    
    // Set skills from job description (available skills)
    setSelectedSkills(availableSkills.length > 0 ? availableSkills : []);
    
    // Set question type based on the editing round
    setSelectedQuestionType(questionType);
    
    // Clear question style and difficulty as they weren't stored in the round
    setSelectedQuestionStyle("");
    setSelectedDifficulty("");
  };

  return {
    // Skills state
    selectedSkills,
    setSelectedSkills,
    customSkillInput,
    setCustomSkillInput,
    handleSkillToggle,
    handleSelectAllSkills,
    handleClearAllSkills,
    handleAddCustomSkill,
    
    // Question configuration
    questionStyles,
    selectedQuestionType,
    selectedQuestionStyle,
    selectedDifficulty,
    filteredQuestionStyles,
    handleQuestionTypeChange,
    handleQuestionTypeChangeFromForm,
    handleQuestionStyleChange,
    handleDifficultyChange,
    
    // Generation state
    isLoadingStyles,
    isGeneratingTemplate,
    isTemplateGeneratorReady,
    handleGenerateTemplate,
    
    // Generation progress
    generationStep,
    generationSteps,
    
    // Utility functions
    resetState,
    cancelGeneration,
    initializeFromJobDescription,
    initializeFromEditingRound,
  };
};

export default useTemplateGenerator;

